import React, {
  ChangeEvent,
  Dispatch,
  SetStateAction,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import { toast } from 'react-toastify';
import { attachFiles, catchErrorMessage, splitFilesByType } from 'utility/helpers';
import KnowledgeAPI from 'api/KnowledgeAPI';
import useUserData, { UserType } from 'hooks/useUserData';
import { AttachmentType } from 'pages/knowledge/hooks/useArticlesQuery';
import LightboxModal from 'components/common/modal/LightboxModal';
import { useSearchItemQuery } from 'hooks/useSearchQuery';
import RenderThumbnails from '@buildwise/pages/companyFiles/components/RenderThumbnails';
import { AUDIO_TYPES, IMAGE_TYPES, VIDEO_TYPES } from 'components/utils/constants';
import CommentInput from '@buildwise/pages/article/components/CommentInput';
import { MentionType } from '@buildwise/pages/article/components/CommentsBox';

type UsersSearchResponseType = {
  totalElements?: number;
  totalNumber?: number;
  totalPages?: number;
  userResponse: UserType[];
};

type FacilitatorNotesProps = {
  facilitatorNotesState: {
    facilitatorNotes: string;
    facilitatorMentions: MentionType[];
    facilitatorAttachments: AttachmentType[];
    isSubmittingFacilitatorNotes: boolean;
    setFacilitatorNotes: Dispatch<SetStateAction<string>>;
    setFacilitatorMentions: Dispatch<SetStateAction<MentionType[]>>;
    setFacilitatorAttachments: Dispatch<SetStateAction<AttachmentType[]>>;
    setSubmittingFacilitatorNotes: Dispatch<SetStateAction<boolean>>;
  };
};

function FacilitatorNotes({
  facilitatorNotesState: {
    facilitatorNotes,
    facilitatorAttachments,
    isSubmittingFacilitatorNotes,
    setFacilitatorNotes,
    setFacilitatorMentions,
    setFacilitatorAttachments,
  },
}: FacilitatorNotesProps) {
  const { user, companyId } = useUserData();
  const { data: people } = useSearchItemQuery<UsersSearchResponseType>(
    {
      searchType: 'users',
      operator: 'or',
      filterBy: {
        filters: [
          {
            filterType: 'name',
            filterValue: 'ALL',
          },
        ],
      },
    },
    true,
  );
  const [lightboxOpen, setLightboxOpen] = useState<{
    open: boolean;
    fileName: string;
  }>({
    open: false,
    fileName: '',
  });
  const userData = useMemo(() => {
    return (
      people?.userResponse.map((user) => ({
        ...user,
        name: user.userNameToDisplay,
        id: user.username,
        display: user.userNameToDisplay,
        userId: user.id,
      })) || []
    );
  }, [people]);
  const { mediaFiles, thumbnails } = useMemo(() => {
    const mediaFiles: AttachmentType[] = [];
    const thumbnails: AttachmentType[] = [];

    facilitatorAttachments?.forEach((attachment) => {
      let fileType = `${attachment.fileType.toLowerCase().split('/')?.[1]}`;
      if (attachment.fileUploaded) {
        fileType = attachment.fileType.toLowerCase();
      }

      const isMediaFile =
        IMAGE_TYPES.includes(`image/${fileType}`) ||
        VIDEO_TYPES.includes(`video/${fileType}`) ||
        AUDIO_TYPES.includes(`audio/${fileType}`);

      if (isMediaFile && !attachment?.isRemoved) {
        mediaFiles.push(attachment);
      }

      if (!attachment?.isRemoved) {
        thumbnails.push(attachment);
      }
    });

    return { mediaFiles, thumbnails };
  }, [facilitatorAttachments]);

  const onFileInputChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const files = [...((event?.target?.files || []) as File[])];
      attachFiles({
        newAttachments: files,
        attachments: facilitatorAttachments,
        setAttachments: setFacilitatorAttachments,
      });
    },
    [facilitatorAttachments],
  );

  const removeAttachment = useCallback(
    (file: AttachmentType) => {
      if (!isSubmittingFacilitatorNotes) {
        const updatedAttachments = [...facilitatorAttachments];
        const index = updatedAttachments.findIndex(
          (attachment) => attachment.fileName.toLowerCase() === file.fileName.toLowerCase(),
        );
        if (file.fileUploaded) {
          updatedAttachments[index] = {
            ...updatedAttachments[index],
            isRemoved: true,
          };
        } else {
          updatedAttachments.splice(index, 1);
        }

        setFacilitatorAttachments(updatedAttachments);
      }
    },
    [facilitatorAttachments, isSubmittingFacilitatorNotes],
  );

  const updateAttachment = (fileName: string, updatedAttachment: AttachmentType) => {
    const updatedAttachments = [...facilitatorAttachments];
    const index = updatedAttachments.findIndex((file) => file.fileName === fileName);

    if (index > -1) {
      updatedAttachments[index] = updatedAttachment;
      setFacilitatorAttachments(updatedAttachments);
    }
  };

  return (
    <>
      <CommentInput
        attachments={facilitatorAttachments}
        onFileInputChange={onFileInputChange}
        value={facilitatorNotes}
        setValue={setFacilitatorNotes}
        setMentions={setFacilitatorMentions}
        user={user}
        userData={userData}
        loading={isSubmittingFacilitatorNotes}
        hideAvatar
        style={{ minHeight: 100 }}
        placeholder="Add notes"
        autoFocusWhenEdit={false}
      />
      <RenderThumbnails
        files={thumbnails}
        removeFile={removeAttachment}
        setLightboxOpen={setLightboxOpen}
        showEdit
        showEditLogoOnly
        defaultGridMdSize={12}
        isImage={(file: AttachmentType) => {
          let fileType = `${file.fileType.toLowerCase().split('/')?.[1]}`;
          if (file.fileUploaded) {
            fileType = file.fileType.toLowerCase();
          }
          return IMAGE_TYPES.includes(
            file.fileUploaded ? `image/${fileType}` : file.fileType.toLowerCase(),
          );
        }}
      />

      {lightboxOpen.open && (
        <LightboxModal
          onClose={() => setLightboxOpen({ open: false, fileName: '' })}
          open={lightboxOpen.open}
          mode="edit"
          mediaFiles={mediaFiles}
          defaultOpenFileName={lightboxOpen.fileName}
          updateFile={updateAttachment}
          hideCoverPhotoOption
        />
      )}
    </>
  );
}

export default FacilitatorNotes;
